import clsx from 'clsx';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { ROW_FINANCIALS_OVERFLOW_MARKER } from './EntityHoverCard.constants';
import type { HoverFinancialValue, RowFinancialsDetails as RowFinancials } from './EntityHoverCard.types';
import { useFittedMetricCount } from './useFittedMetricCount';
import EntityHoverCardTooltip from './EntityHoverCardTooltip';
import type { EntityHoverRow } from './toEntityHoverProfile';
import styles from './RowFinancialsDetails.module.scss';

/** Details-column body for an on-AICA row: Revenue / PAT / Net-Worth, or "Locked" that opens the row's hover card. */
function RowFinancialsDetails({ details, row }: { details: RowFinancials; row: EntityHoverRow }) {
    if (details.kind === 'locked')
        return (
            <EntityHoverCardTooltip row={row}>
                <span className={styles.Locked}>{details.text}</span>
            </EntityHoverCardTooltip>
        );
    return <FittedMetrics values={details.values} text={details.text} />;
}

function Metric({ label, value }: HoverFinancialValue) {
    return (
        <span className={styles.Metric}>
            <span className={styles.Label}>{label}</span>
            <span>{value}</span>
        </span>
    );
}

function OverflowMarker() {
    return (
        <span className={styles.Overflow}>
            <span />
            <span>{ROW_FINANCIALS_OVERFLOW_MARKER}</span>
        </span>
    );
}

function FittedMetrics({ values, text }: { values: HoverFinancialValue[]; text: string }) {
    const { frameRef, measureRef, fit } = useFittedMetricCount(values.length, text);
    return (
        <div ref={frameRef} className={styles.Frame}>
            <Tooltip
                content={text}
                direction="top"
                contentCss={{ whiteSpace: 'nowrap' }}
                wrapperClassName={styles.TooltipTrigger}
                fitContent
                small
                usePortal
            >
                <span className={styles.Metrics} aria-label={text}>
                    {values.slice(0, fit.count).map((metric) => (
                        <Metric key={metric.label} {...metric} />
                    ))}
                    {fit.overflow && <OverflowMarker />}
                </span>
            </Tooltip>
            <span ref={measureRef} className={clsx(styles.Metrics, styles.Measure)} aria-hidden="true">
                {values.map((metric) => (
                    <Metric key={metric.label} {...metric} />
                ))}
                <OverflowMarker />
            </span>
        </div>
    );
}

export default RowFinancialsDetails;
